import { useEffect, useRef, useState } from 'react'
import { YouTubePlayer } from 'youtube-player/dist/types'

type PlayPauseEvent = {
  action: 'play' | 'pause' | 'end'
  timestamp: number
  currentTime: number
}

interface YouTubePlayerProps extends YouTubePlayer {
  getVideoData: () => {
    title: string
  }
}

type UseYouTubeEmbedParams = {
  videoId: string
  startSeconds?: number
  pollInterval?: number
  onPlayPauseLog?: (event: PlayPauseEvent) => void
}

type UseYouTubeEmbedReturn = {
  currentTime: number
  duration: number | null
  title: string
  playerRef: React.MutableRefObject<YouTubePlayer | null>
  ytProps: {
    videoId: string
    onReady: (event: any) => void
    onPlay: () => void
    onPause: () => void
    onEnd: () => void
    opts: {
      playerVars: {
        start: number
      }
    }
  }
}

const useYouTubeEmbed = ({
  videoId,
  startSeconds = 0,
  pollInterval = 500,
  onPlayPauseLog
}: UseYouTubeEmbedParams): UseYouTubeEmbedReturn => {
  const playerRef = useRef<YouTubePlayer | null>(null)
  const [currentTime, setCurrentTime] = useState<number>(0)
  const [duration, setDuration] = useState<number | null>(null)
  const [title, setTitle] = useState<string>('')
  const [isReady, setIsReady] = useState<boolean>(false)

  useEffect(() => {
    let interval: ReturnType<typeof setInterval>
    if (playerRef.current) {
      interval = setInterval(async () => {
        if (playerRef.current?.getCurrentTime) {
          try {
            const time = await playerRef.current.getCurrentTime()
            setCurrentTime(parseFloat(time.toFixed(2)))
          } catch (error) {
            console.log('Failed to get current time:', error)
          }
        }
      }, pollInterval)
    }
    return () => {
      clearInterval(interval)
    }
  }, [pollInterval, isReady])

  const onReady = async (event: { target: YouTubePlayerProps }) => {
    const player = event.target as YouTubePlayerProps
    playerRef.current = player

    if (startSeconds >= 0) {
      player.seekTo(startSeconds, true)
    }

    try {
      const duration = await player.getDuration()
      const videoTitle = player.getVideoData().title

      setDuration(duration)
      setTitle(videoTitle)
      setIsReady(true)
    } catch (error) {
      console.warn('Failed to get video metadata:', error)
    }
  }

  const onPlay = () => {
    onPlayPauseLog?.({ action: 'play', timestamp: Date.now(), currentTime })
  }

  const onPause = () => {
    onPlayPauseLog?.({ action: 'pause', timestamp: Date.now(), currentTime })
  }

  const onEnd = () => {
    onPlayPauseLog?.({ action: 'end', timestamp: Date.now(), currentTime })
  }

  return {
    currentTime,
    duration,
    title,
    playerRef,
    ytProps: {
      videoId,
      onReady,
      onPlay,
      onPause,
      onEnd,
      opts: {
        playerVars: {
          start: startSeconds
        }
      }
    }
  }
}

export default useYouTubeEmbed
